const ARTICLE_SELECTOR = "main#MainContent";

/**
 * Turn heading text into a clean URL-friendly ID.
 */
function createHeadingId(text, index) {
  const slug = text
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return slug || `heading-${index + 1}`;
}

/**
 * Ensure heading IDs remain unique.
 */
function getUniqueHeadingId(baseId) {
  let id = baseId;
  let counter = 2;

  while (document.getElementById(id)) {
    id = `${baseId}-${counter}`;
    counter += 1;
  }

  return id;
}

/**
 * Return only headings that appear after the TOC heading.
 */
function getArticleHeadings(
  tableOfContents,
  headingSelector,
  excludeSelector = ""
) {
  const tocHeading = tableOfContents.querySelector(
    ".js-tab-of-con-heading"
  );

  if (!tocHeading || !headingSelector) {
    return [];
  }

  return Array.from(
    document.querySelectorAll(
      `${ARTICLE_SELECTOR} :is(${headingSelector})`
    )
  ).filter((heading) => {
    // Exclude headings inside the TOC itself.
    if (tableOfContents.contains(heading)) {
      return false;
    }

    // Exclude headings inside configured sections/components.
    if (
      excludeSelector &&
      heading.closest(excludeSelector)
    ) {
      return false;
    }

    // Only include headings that appear after the TOC.
    return Boolean(
      tocHeading.compareDocumentPosition(heading) &
        Node.DOCUMENT_POSITION_FOLLOWING
    );
  });
}

/**
 * Scroll to a selected heading.
 */
function handleLinkClick(event) {
  event.preventDefault();

  const link = event.currentTarget;
  const targetSelector = link.getAttribute("href");

  if (!targetSelector) {
    return;
  }

  const target = document.querySelector(targetSelector);

  if (!target) {
    return;
  }

  const tableOfContents = link.closest(".js-tab-of-con");

  if (!tableOfContents) {
    return;
  }

  const breakpoint =
    Number.parseInt(tableOfContents.dataset.breakpoint, 10) || 768;

  const desktopOffset =
    Number.parseInt(tableOfContents.dataset.offset, 10) || 0;

  const mobileOffset =
    Number.parseInt(tableOfContents.dataset.mobileOffset, 10) || 0;

  const offset =
    window.innerWidth > breakpoint ? desktopOffset : mobileOffset;

  const targetPosition =
    target.getBoundingClientRect().top +
    window.pageYOffset -
    offset;

  const requestedBehaviour =
    tableOfContents.dataset.scrollBehaviour;

  const scrollBehaviour =
    requestedBehaviour === "smooth" ? "smooth" : "auto";

  window.scrollTo({
    top: targetPosition,
    behaviour: scrollBehaviour,
    behavior: scrollBehaviour
  });

  history.pushState(null, "", targetSelector);
}

/**
 * Expand a nested TOC list.
 */
function expandItem(item, childList, button) {
  item.classList.add("tab-of-con__item--expanded");
  button.setAttribute("aria-expanded", "true");

  childList.style.height = "0px";

requestAnimationFrame(() => {
    childList.style.height = childList.scrollHeight + "px";
});

  const finishExpansion = () => {
    if (
      item.classList.contains("tab-of-con__item--expanded")
    ) {
      childList.style.height = "auto";
    }

    childList.removeEventListener(
      "transitionend",
      finishExpansion
    );
  };

  childList.addEventListener(
    "transitionend",
    finishExpansion
  );
}

/**
 * Collapse a nested TOC list.
 */
function collapseItem(item, childList, button) {

    // If the height is auto, lock it to the current pixel height first
    childList.style.height = childList.scrollHeight + "px";

    requestAnimationFrame(() => {

        requestAnimationFrame(() => {
            childList.style.height = "0px";
        });

    });

    item.classList.remove("tab-of-con__item--expanded");
    button.setAttribute("aria-expanded", "false");
}

/**
 * Handle clicks on the dedicated chevron button.
 */
function handleToggleClick(event) {
  event.preventDefault();
  event.stopPropagation();

  const button = event.currentTarget;
  const item = button.closest(
    ".tab-of-con__item--has-children"
  );

  if (!item) {
    return;
  }

  const childList = item.querySelector(
    ":scope > .tab-of-con__item__child-list"
  );

  if (!childList) {
    return;
  }

  const isExpanded = item.classList.contains(
    "tab-of-con__item--expanded"
  );

  if (isExpanded) {
    collapseItem(item, childList, button);
  } else {
    expandItem(item, childList, button);
  }
}

/**
 * Add an accessible expand/collapse button to a parent item.
 */
function addToggleButton(item, headingText) {
  if (item.querySelector(":scope > .tab-of-con__toggle")) {
    return;
  }

  const button = document.createElement("button");

  button.type = "button";
  button.className = "tab-of-con__toggle";
  button.setAttribute("aria-expanded", "false");
  button.setAttribute(
    "aria-label",
    `Show subsections for ${headingText}`
  );

  button.innerHTML =
    '<span class="visually-hidden">Toggle subsections</span>';

  button.addEventListener("click", handleToggleClick);

  const link = item.querySelector(
    ":scope > .tab-of-con__item__link"
  );

  if (link) {
    link.insertAdjacentElement("afterend", button);
  } else {
    item.prepend(button);
  }
}

/**
 * Remove links previously generated by the script.
 */
function clearGeneratedLinks(tableOfContents) {
  tableOfContents
    .querySelectorAll(
      ".js-tab-of-con-link:not(.js-tab-of-con-link-template)"
    )
    .forEach((item) => item.remove());

  tableOfContents
    .querySelectorAll(
      ".js-tab-of-con-list:not(.js-tab-of-con-list-template)"
    )
    .forEach((list) => list.remove());
}

/**
 * Build the TOC hierarchy.
 */
function createLinks(tableOfContents) {
  const config = window.toc_config;

  if (
    !config ||
    !Array.isArray(config.headings) ||
    config.headings.length === 0
  ) {
    tableOfContents.style.display = "none";
    return;
  }
  
const selector = config.headings.join(", ");

const headings = getArticleHeadings(
  tableOfContents,
  selector,
  config.excludeSelector || ""
);

  const linkTemplate = tableOfContents.querySelector(
    ".js-tab-of-con-link-template"
  );

  const childListTemplate = tableOfContents.querySelector(
    ".js-tab-of-con-list-template"
  );

  if (!linkTemplate || !childListTemplate) {
    return;
  }

  clearGeneratedLinks(tableOfContents);

  if (headings.length === 0) {
    tableOfContents.style.display = "none";
    return;
  }

  tableOfContents.style.display = "";

  const firstLevel = Number.parseInt(
    headings[0].tagName.substring(1),
    10
  );

  const stack = [
    {
      level: firstLevel,
      list: tableOfContents,
      lastItem: null
    }
  ];

  headings.forEach((heading, index) => {
    if (!heading.id) {
      const baseId = createHeadingId(
        heading.textContent,
        index
      );

      heading.id = getUniqueHeadingId(baseId);
    }

    const level = Number.parseInt(
      heading.tagName.substring(1),
      10
    );

    while (
      stack.length > 1 &&
      level < stack[stack.length - 1].level
    ) {
      stack.pop();
    }

    if (level > stack[stack.length - 1].level) {
      const parentEntry = stack[stack.length - 1];
      const parentItem = parentEntry.lastItem;

      if (parentItem) {
        const childList =
          childListTemplate.cloneNode(true);

        childList.classList.remove(
          "js-tab-of-con-list-template"
        );

        parentItem.appendChild(childList);
        parentItem.classList.add(
          "tab-of-con__item--has-children"
        );

        const parentLink = parentItem.querySelector(
          ":scope > .tab-of-con__item__link"
        );

        addToggleButton(
          parentItem,
          parentLink
            ? parentLink.textContent.trim()
            : "this section"
        );

        stack.push({
          level,
          list: childList,
          lastItem: null
        });
      }
    }

    const currentEntry = stack[stack.length - 1];
    const item = linkTemplate.cloneNode(true);

    item.classList.remove(
      "js-tab-of-con-link-template"
    );

    const link = item.querySelector(
      ".js-tab-of-con-link-href"
    );

    link.href = `#${heading.id}`;
    link.textContent = heading.textContent.trim();
    link.addEventListener("click", handleLinkClick);

    currentEntry.list.appendChild(item);
    currentEntry.lastItem = item;
  });

  if (config.collapsible) {
    tableOfContents
      .querySelectorAll(
        ".tab-of-con__item__child-list:not(.js-tab-of-con-list-template)"
      )
      .forEach((list) => {
        list.style.height = "0px";
      });
  } else {
    tableOfContents
      .querySelectorAll(".tab-of-con__toggle")
      .forEach((button) => {
        button.hidden = true;
      });

    tableOfContents
      .querySelectorAll(
        ".tab-of-con__item__child-list:not(.js-tab-of-con-list-template)"
      )
      .forEach((list) => {
        list.style.height = "auto";
      });
  }
}

/**
 * Initialise every TOC currently on the page.
 */
function initTableOfContents(root = document) {
  root
    .querySelectorAll(".js-tab-of-con")
    .forEach((tableOfContents) => {
      createLinks(tableOfContents);
    });
}

if (document.readyState === "loading") {
  document.addEventListener(
    "DOMContentLoaded",
    () => initTableOfContents(),
    { once: true }
  );
} else {
  initTableOfContents();
}

/**
 * Rebuild a TOC when Shopify reloads its section
 * inside the Theme Editor.
 */
if (window.Shopify && Shopify.designMode) {
  document.addEventListener(
    "shopify:section:load",
    (event) => {
      window.setTimeout(() => {
        initTableOfContents(event.target);
      }, 100);
    }
  );
}